import {
  isGuestWorkspaceRole,
  useCurrentAccount,
  useCurrentWorkspace,
  useRoles,
} from '@gripp/shared-logic';
import { LoadingIndicator } from '@gripp/shared-ui';
import type { ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';

/**
 * Wraps every asset sub-route that is off a Guest's allowlist (plan 0030
 * Phase 5: the header plus their own conversation, nothing else).
 * `ProtectedRoute`'s `requiredPermission` check runs against the caller's
 * AMBIENT role -- whatever workspace they are currently switched into, e.g.
 * My History or their own personal workspace -- not the asset's own
 * workspace, so it cannot tell a Guest of THIS asset apart from a member
 * elsewhere holding the same permission name. This re-checks membership
 * scoped to the workspace the sub-route is actually being read in.
 *
 * That workspace comes from the route prefix, else the active workspace --
 * NOT from `useWorkspaceAsset(assetId)`. That hook queries by asset id alone
 * and reads an arbitrary `items[0]`, so on an asset shared across workspaces
 * it decided guest-ness against whichever row Mongo happened to return
 * first. Because `isGuestWorkspaceRole` reads "no role entry for that
 * workspace" as not-a-guest, landing on a workspace the caller does not
 * belong to admitted them; landing on one where they hold Guest turned a
 * legitimate member away. `useAssetChatSubject` and `AssetChatHeaderButton`
 * already resolve the workspace this way for the same reason.
 *
 * A redirect can't be undone from the user's side, so it only fires on a
 * RESOLVED, positive guest determination -- unresolved account, roles or
 * workspace hold on a loading state instead, they never navigate.
 */
export const AssetSubRouteGuestGate = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { workspaceId: routeWorkspaceId } = useParams();
  const currentWorkspace = useCurrentWorkspace();
  const { account, isLoading: isAccountLoading } = useCurrentAccount();
  const { roles, isLoading: isRolesLoading } = useRoles();

  const gateWorkspaceId = routeWorkspaceId ?? currentWorkspace?.id;
  const isResolved =
    !isAccountLoading && !isRolesLoading && !!gateWorkspaceId && !!account;

  if (!isResolved) {
    return <LoadingIndicator flexContainer />;
  }

  if (isGuestWorkspaceRole(gateWorkspaceId, account?.workspaceRoles, roles)) {
    return <Navigate to="/forbidden" replace />;
  }

  return children;
};
